// Edition interface for GameBlock

import { useState, useMemo, useCallback } from 'react';
import moment from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Select from './Select';
import InputDate from './InputDate';
import Input from './Input';
import MultiSelect from './MultiSelect';
import Spinner from './Spinner';
import RadioButton from './RadioButton';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import { overlapCheck } from './Field';
import {
  time24To12H,
  time12To24H,
  hourTo12H,
  parseTimeToMinutes,
  getTeamDivision,
} from '../utils/misc';
import {
  defaultDuration,
  scheduleStartHour,
  scheduleEndHour,
  scheduleStepMin,
} from '../utils/consts';
import { toast } from 'react-toastify';
import { tournamentGamesCreate, tournamentGameUpdate } from '../utils/firebase';
import { GameTypes, PlayoffRounds } from '../utils/enums';
import { useDivisions, useRefereeOptions } from '../utils/hooks';
import './GameBlockEdit.scss';

// Find the first available time slot and field for a game
function findAvailableSlot({ games, fields, date, fromTime, duration }) {
  // filter games for the given day
  const dayGames = games.filter((g) => g.date === date);

  // group games by field id
  const gamesByField = {};
  dayGames.forEach((g) => {
    const startMoment = moment(`${g.date} ${g.start}`, 'YYYY-MM-DD HH:mm');
    if (!gamesByField[g.field]) {
      gamesByField[g.field] = [];
    }
    gamesByField[g.field].push({
      start: startMoment,
      end: startMoment.clone().add(g.duration, 'minutes'),
    });
  });

  // sort fields by sortIndex
  const sortedFields = [...fields].sort((a, b) => a.sortIndex - b.sortIndex);

  // determine day boundaries
  const searchStart = moment(
    `${date} ${fromTime ?? String(scheduleStartHour).padStart(2, '0')}:00`,
    'YYYY-MM-DD HH:mm',
  );
  const searchEnd = moment(
    `${date} ${String(scheduleEndHour).padStart(2, '0')}:00`,
    'YYYY-MM-DD HH:mm',
  );

  let cursor = searchStart.clone();

  // iterate through day by step increment
  while (cursor.clone().add(duration, 'minutes').isSameOrBefore(searchEnd)) {
    const slotEnd = cursor.clone().add(duration, 'minutes');

    // check each field for free slot
    for (const fieldInfo of sortedFields) {
      const occupied = (gamesByField[fieldInfo.id] || []).some(
        ({ start, end }) => cursor.isBefore(end) && slotEnd.isAfter(start),
      );
      if (!occupied) {
        return {
          availableTime: cursor.format('HH:mm'),
          availableField: fieldInfo.id,
        };
      }
    }

    cursor.add(scheduleStepMin, 'minutes');
  }

  // no slot found
  return { availableTime: null, availableField: null };
}

export default function GameBlockEdit({ id, game, setBracketsPopup }) {
  const { closeModal, isModalOpen } = useModal();
  const [spinner, setSpinner] = useState(false);
  const { tournamentsById, teamsById, coachesByTeamId } = useFirebase();
  // The body outlives the closed modal, so its reads stand down with it
  const refereeOptions = useRefereeOptions(
    isModalOpen ? id : null,
    game?.referees,
  );
  const tournament = tournamentsById.get(id);
  const { games, fields, startDate, endDate } = tournament;
  const {
    date,
    duration,
    field,
    start,
    team1,
    team2,
    type,
    division,
    round,
    placement,
  } = game || {};

  const divisions = useDivisions(id);

  // A name is resolved inside the tournament instead of by scanning the
  // organization, whose teams are no longer read as a list at all. Not
  // narrowed to the admitted ones the option lists offer: an existing game
  // keeps the team it was scheduled with, and saving would null it out.
  const findTournamentTeamByName = useCallback(
    (name) =>
      (tournament.teams || [])
        .map((t) => teamsById.get(t.id))
        .find((team) => team?.name === name),
    [tournament.teams, teamsById],
  );

  const { availableField, availableTime } = useMemo(() => {
    return findAvailableSlot({
      games,
      fields,
      date: moment(date).format('YYYY-MM-DD'),
      fromTime: `${scheduleStartHour}:00`,
      duration: defaultDuration,
    });
  }, [date, fields, games]);

  const getFieldNameById = (id) => fields.find((f) => f.id === id)?.name;

  const initialValues = {
    date: date ? moment(date).toDate() : null,
    duration: duration || defaultDuration,
    field: field
      ? getFieldNameById(field)
      : availableField
        ? getFieldNameById(availableField)
        : null,
    referees: game?.referees ?? [],
    start: start
      ? time24To12H(start)
      : availableTime
        ? time24To12H(availableTime)
        : hourTo12H(scheduleStartHour),
    team1: team1 ? teamsById.get(team1)?.name : 'TBD',
    team2: team2 ? teamsById.get(team2)?.name : 'TBD',
    type: type || GameTypes.POOLGAME,
    division: division || '',
    round: round || '',
    placement: placement || '',
  };

  const timeRegex = /^ *0?(1[0-2]|[1-9]) *: *[0-5][0-9] *(am|pm) *$/i;

  function overlapValidator(values, conflictKeys) {
    // If any required value is missing, skip validation
    const { date, start, duration, field, team1, team2 } = values;
    if (!date || !start || !duration || !field) return true;
    if (!timeRegex.test(start)) return true;

    // Normalize conflictKeys to an array
    const keys = Array.isArray(conflictKeys) ? conflictKeys : [conflictKeys];

    // Find the ID of the selected field
    const fieldId = fields.find((f) => f.name === field)?.id;

    // Find the IDs of the selected teams
    const t1 = findTournamentTeamByName(team1)?.id;
    const t2 = findTournamentTeamByName(team2)?.id;

    const refIds = (values.referees || []).filter(Boolean);

    // Filter games occurring on the same date
    const gamesOnDate = games.filter(
      (g) =>
        moment(g.date).format('YYYY-MM-DD') ===
        moment(date).format('YYYY-MM-DD'),
    );

    // Determine if we should do a generic check (no team1 and no team2 in conflictKeys)
    const hasGenericCheck = !keys.includes('team1') && !keys.includes('team2');
    let newTeams = [];
    if (hasGenericCheck) {
      // If checking slot/referees/etc., include both teams
      newTeams = [t1, t2].filter(Boolean);
    } else {
      // If only checking individual teams, include accordingly
      if (keys.includes('team1') && t1) newTeams.push(t1);
      if (keys.includes('team2') && t2) newTeams.push(t2);
    }

    // Perform the overlap check
    const overlap = overlapCheck({
      games: gamesOnDate,
      gameId: game?.id,
      field: fieldId,
      start: time12To24H(start),
      duration: Number(duration),
      coachesByTeamId,
      referees: refIds,
      newTeams,
    });

    // Iterate through each key; return false on any conflict
    for (const key of keys) {
      if (key === 'team1' || key === 'team2') {
        if (overlap.team) return false;
      } else {
        if (overlap[key]) return false;
      }
    }

    // No conflicts found
    return true;
  }

  const maxPlacementForRound = {
    [PlayoffRounds.R128]: 64,
    [PlayoffRounds.R64]: 32,
    [PlayoffRounds.R32]: 16,
    [PlayoffRounds.R16]: 8,
    [PlayoffRounds.QF]: 4,
    [PlayoffRounds.SF]: 2,
    [PlayoffRounds.F]: 1,
  };

  const validationSchema = new Yup.ObjectSchema({
    type: Yup.string().required('Required'),
    division: Yup.string().when('type', {
      is: GameTypes.PLAYOFF,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    round: Yup.string()
      .oneOf(Object.keys(PlayoffRounds), 'Invalid')
      .when('type', {
        is: GameTypes.PLAYOFF,
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    placement: Yup.string()
      .matches(/^[1-9]\d*$/, 'Invalid')
      .when('type', {
        is: GameTypes.PLAYOFF,
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.notRequired(),
      })
      .test('duplicate-playoff', 'Duplicate playoff game', function (value) {
        const { type, division, round } = this.parent;
        if (type !== GameTypes.PLAYOFF) return true;
        if (!division || !round || !value) return true;
        return !games.some(
          (g) =>
            g.id !== game?.id &&
            g.type === GameTypes.PLAYOFF &&
            g.division === division &&
            g.round === round &&
            g.placement === +value,
        );
      })
      .test('placement-exceeds-round', '', function (value) {
        const { type, round } = this.parent;
        if (type !== GameTypes.PLAYOFF) return true;
        if (!round || !value) return true;
        const max = maxPlacementForRound[round];
        if (max === undefined) return true;
        if (+value > max)
          return this.createError({ message: `Max ${max} for this round` });
        return true;
      }),
    date: Yup.date()
      .required('Required')
      .test('overlap', 'Slot is busy', function (value) {
        return overlapValidator({ ...this.parent, date: value }, 'slot');
      }),
    start: Yup.string()
      .matches(/^ *0?(1[0-2]|[1-9]) *: *[0-5][0-9] *(am|pm) *$/i, 'Invalid')
      .test('within-schedule-range', 'Out of range', function (value) {
        if (!value) return true;
        if (!/^ *0?(1[0-2]|[1-9]) *: *[0-5][0-9] *(am|pm) *$/i.test(value))
          return true;

        const valueMinutes = parseTimeToMinutes(value);
        const startMinutes = scheduleStartHour * 60;
        const endMinutes = scheduleEndHour * 60 - scheduleStepMin;

        return valueMinutes >= startMinutes && valueMinutes <= endMinutes;
      })
      .test('overlap', 'Slot is busy', function (value) {
        return overlapValidator({ ...this.parent, start: value }, 'slot');
      })
      .required('Required'),
    duration: Yup.number()
      .typeError('Invalid')
      .integer('Invalid')
      .positive('Invalid')
      .test('within-end-time', 'Out of range', function (value) {
        if (!value) return true;
        if (
          !/^ *0?(1[0-2]|[1-9]) *: *[0-5][0-9] *(am|pm) *$/i.test(
            this.parent.start,
          )
        )
          return true;

        const startMinutes = parseTimeToMinutes(this.parent.start);
        const endScheduleMinutes = scheduleEndHour * 60;

        return startMinutes + value <= endScheduleMinutes;
      })
      .test('overlap', 'Slot is busy', function (value) {
        return overlapValidator({ ...this.parent, duration: value }, 'slot');
      })
      .required('Required'),
    team1: Yup.string()
      .required('Required')
      .test('not-same-except-tbd', 'The same', function (value) {
        const team2 = this.parent.team2;
        if (value === 'TBD' && team2 === 'TBD') return true;
        return value !== team2;
      })
      .test('team-busy-1', 'Team is busy', function (val) {
        return overlapValidator({ ...this.parent, team1: val }, 'team1');
      })
      .test('coach-busy-1', 'Coach is busy', function (val) {
        return overlapValidator({ ...this.parent, team1: val }, [
          'team1',
          'coach',
        ]);
      }),
    team2: Yup.string()
      .required('Required')
      .test('not-same-except-tbd', 'The same', function (value) {
        const team1 = this.parent.team1;
        if (value === 'TBD' && team1 === 'TBD') return true;
        return value !== team1;
      })
      .test('team-busy-2', 'Team is busy', function (val) {
        return overlapValidator({ ...this.parent, team2: val }, 'team2');
      })
      .test('coach-busy-2', 'Coach is busy', function (val) {
        return overlapValidator({ ...this.parent, team2: val }, [
          'team2',
          'coach',
        ]);
      }),
    field: Yup.string()
      .required('Required')
      .test('overlap', 'Slot is busy', function (val) {
        return overlapValidator({ ...this.parent, field: val }, 'slot');
      }),
    referees: Yup.array().test('ref-busy', 'Busy at the time', function (val) {
      return overlapValidator({ ...this.parent, referees: val }, 'referee');
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSpinner(true);
      const payload = {
        id,
        date: moment(values.date).format('YYYY-MM-DD'),
        start: time12To24H(values.start),
        duration: +values.duration,
        field: fields.find((f) => f.name === values.field)?.id,
        referees: values.referees,
        team1: findTournamentTeamByName(values.team1)?.id || null,
        team2: findTournamentTeamByName(values.team2)?.id || null,
        type: values.type,
        round: values.round || null,
        placement: values.placement ? +values.placement : null,
        division: values.division || undefined,
      };
      try {
        if (game.id) {
          await tournamentGameUpdate({ ...payload, gameId: game.id });
        } else {
          const { id: _tourId, ...gameData } = payload;
          await tournamentGamesCreate({ id, games: [gameData] });
        }
        closeModal();
      } catch (e) {
        toast.error(e?.message ?? 'Failed to save game');
      } finally {
        setSpinner(false);
      }
    },
  });

  const { team1Options, team2Options } = useMemo(() => {
    const getAdmittedTeamNamesInDivision = (div) =>
      tournament.teams
        .filter((t) => t.admitted)
        .filter((t) => getTeamDivision(teamsById.get(t.id)) === div)
        .map((t) => teamsById.get(t.id)?.name)
        .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));

    const fullTeamOptions = [
      'TBD',
      ...tournament.teams
        .filter((t) => t.admitted)
        .map((t) => teamsById.get(t.id)?.name)
        .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })),
    ];
    let team1Options = fullTeamOptions;
    let team2Options = fullTeamOptions;

    if (formik.values.type === GameTypes.PLAYOFF) {
      const div = formik.values.division;
      if (div) {
        const names = getAdmittedTeamNamesInDivision(div);
        team1Options = ['TBD', ...names];
        team2Options = ['TBD', ...names];
      }
    } else {
      if (formik.values.team1 !== 'TBD') {
        const div = getTeamDivision(
          findTournamentTeamByName(formik.values.team1),
        );
        const names = getAdmittedTeamNamesInDivision(div);
        team2Options = ['TBD', ...names];
      }

      if (formik.values.team2 !== 'TBD') {
        const div = getTeamDivision(
          findTournamentTeamByName(formik.values.team2),
        );
        const names = getAdmittedTeamNamesInDivision(div);
        team1Options = ['TBD', ...names];
      }
    }

    return {
      team1Options,
      team2Options,
    };
  }, [
    formik.values.division,
    formik.values.team1,
    formik.values.team2,
    formik.values.type,
    findTournamentTeamByName,
    tournament.teams,
    teamsById,
  ]);

  return (
    <form className='game-block-edit' onSubmit={formik.handleSubmit}>
      <div className='timing'>
        <InputDate
          name='date'
          value={formik.values.date}
          onChange={(d) => formik.setFieldValue('date', d)}
          minDate={moment(startDate).toDate()}
          maxDate={moment(endDate).toDate()}
          placeholder='Date'
          error={formik.touched.date && formik.errors.date}
        />
        <div className='start-duration'>
          <Input
            name='start'
            placeholder='Time'
            {...formik.getFieldProps('start')}
            error={formik.touched.start && formik.errors.start}
          />
          <div className='duration-wrap'>
            <Input
              className='duration'
              name='duration'
              placeholder='Duration'
              {...formik.getFieldProps('duration')}
              error={formik.touched.duration && formik.errors.duration}
            />
            <p className='min'>min</p>
          </div>
        </div>
      </div>
      <div className='field-ref-wrap'>
        <Select
          options={fields.map((f) => f.name)}
          value={formik.values.field}
          onChange={(v) => formik.setFieldValue('field', v)}
          placeholder='Field'
          error={formik.touched.field && formik.errors.field}
        />
        <MultiSelect
          options={refereeOptions}
          value={formik.values.referees}
          onChange={(v) => formik.setFieldValue('referees', v)}
          placeholder='Referees'
          error={formik.touched.referees && formik.errors.referees}
          search
        />
      </div>
      <div className='teams'>
        <Select
          options={team1Options}
          value={formik.values.team1}
          onChange={(v) => formik.setFieldValue('team1', v)}
          placeholder='First team'
          error={formik.touched.team1 && formik.errors.team1}
          search
        />
        <Select
          options={team2Options}
          value={formik.values.team2}
          onChange={(v) => formik.setFieldValue('team2', v)}
          placeholder='Second team'
          error={formik.touched.team2 && formik.errors.team2}
          search
        />
      </div>
      <div className='type'>
        <RadioButton
          label='Pool game'
          name='type'
          value={GameTypes.POOLGAME}
          selectedValue={formik.values.type}
          onChange={() => formik.setFieldValue('type', GameTypes.POOLGAME)}
        />
        <RadioButton
          label='Playoff'
          name='type'
          value={GameTypes.PLAYOFF}
          selectedValue={formik.values.type}
          onChange={() => formik.setFieldValue('type', GameTypes.PLAYOFF)}
        />
      </div>
      {formik.values.type === GameTypes.PLAYOFF && (
        <div className='playoff show'>
          <Select
            options={divisions}
            value={formik.values.division}
            onChange={(v) => formik.setFieldValue('division', v)}
            placeholder='Division'
            error={formik.touched.division && formik.errors.division}
          />
          <div className='round-placement'>
            <Select
              className='round'
              options={Object.keys(PlayoffRounds).reverse()}
              value={formik.values.round}
              onChange={(v) => formik.setFieldValue('round', v)}
              placeholder='Round'
              error={formik.touched.round && formik.errors.round}
            />
            <Input
              className='placement'
              name='placement'
              placeholder='Placement'
              {...formik.getFieldProps('placement')}
              error={formik.touched.placement && formik.errors.placement}
            />
          </div>
          {setBracketsPopup && (
            <button
              type='button'
              className='transparent small'
              onClick={() => {
                setBracketsPopup({
                  highlightRound: formik.values.round || null,
                  highlightPlacement: formik.values.placement
                    ? Number(formik.values.placement)
                    : null,
                  initDivision: formik.values.division || null,
                  onPick: ({ round, placement }) => {
                    formik.setFieldValue('round', round);
                    formik.setFieldValue('placement', String(placement));
                  },
                });
              }}
            >
              Choose on brackets
            </button>
          )}
        </div>
      )}
      <div className='buttons'>
        <button type='button' className='bw' onClick={closeModal}>
          Cancel
        </button>
        <button type='submit' disabled={spinner}>
          Save
        </button>
      </div>
      <Spinner show={spinner} />
    </form>
  );
}
